// store/cartSlice.js
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { 
  modifyItemsInCartAPI, 
  removeItemFromCart, 
  reduceCartItemQuantity,
  clearCartAPI
} from "../helpers";

export const addToCartWithAPI = createAsyncThunk(
  'cart/addToCartWithAPI',
  async (cartItem, { dispatch, rejectWithValue }) => {
    try {
      console.log("🛒 addToCartWithAPI called with:", {
        product_id: cartItem.product_id,
        variant_id: cartItem.product_variation_id,
        variant_size_id: cartItem.variant_size_id,
        plating_id: cartItem.plating_id,
        quantity: cartItem.quantity,
        maxQty: cartItem.maxQty,
      });

      await modifyItemsInCartAPI(cartItem, dispatch);
      dispatch(addToCart(cartItem));
      return cartItem;
    } catch (error) {
      console.error("❌ addToCartWithAPI error:", error);
      const errorMessage = error.message || error.toString() || "Failed to add to cart";
      return rejectWithValue(errorMessage);
    }
  }
);

export const removeItemFromCartWithAPI = createAsyncThunk(
  'cart/removeItemFromCartWithAPI',
  async (cartItem, { dispatch, rejectWithValue }) => {
    try {
      await removeItemFromCart(cartItem);
      dispatch(removeFromCart(cartItem));
      return cartItem;
    } catch (error) {
      console.error("❌ removeItemFromCartWithAPI error:", error);
      return rejectWithValue(error.response?.data || error.message || "Failed to remove item");
    }
  }
);

export const incrementQuantityWithAPI = createAsyncThunk(
  'cart/incrementQuantityWithAPI',
  async ({ productId, variationId, variantSizeId, platingId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const products = getState().cart.products;

      let product = products.find(
        p =>
          p.product_id === productId &&
          (p.product_variation_id ?? null) === (variationId ?? null) &&
          (p.variant_size_id ?? null) === (variantSizeId ?? null) &&
          (p.plating_id ?? null) === (platingId ?? null)
      );

      if (!product && variantSizeId) {
        product = products.find(
          p =>
            p.product_id === productId &&
            (p.product_variation_id ?? null) === (variationId ?? null) &&
            p.wholesaleSelections?.some(
              s => s.variantSizeId === variantSizeId && 
                   (s.plating_id ?? null) === (platingId ?? null)
            )
        );
      }

      if (!product) {
        console.error("Product not found in Redux store:", { productId, variationId, variantSizeId, platingId });
        throw new Error("Product not found");
      }

      let currentQty = 0;
      let maxQty = null;

      if (product.wholesaleSelections && variantSizeId) {
        const sizeSelection = product.wholesaleSelections.find(
          s => s.variantSizeId === variantSizeId && 
               (s.plating_id ?? null) === (platingId ?? null)
        );
        if (sizeSelection) {
          currentQty = sizeSelection.qty || 0;
          maxQty = sizeSelection.original_stock || sizeSelection.maxQty;
        }
      } else {
        currentQty = product.quantity || 0;
        maxQty = product.maxQty || product.original_stock;
      }

      if (maxQty !== null && maxQty !== undefined) {
        if (currentQty >= maxQty) {
          throw new Error(`Maximum quantity reached (${maxQty} available)`);
        }
      }

      const updated = {
        product_id: productId,
        product_variation_id: variationId,
        variant_size_id: variantSizeId,
        plating_id: platingId,
        quantity: 1,
        metal: product.metal || "",
      };

      await modifyItemsInCartAPI(updated, dispatch);
      dispatch(incrementQuantity({ productId, variationId, variantSizeId, platingId }));
      return updated;
    } catch (error) {
      console.error("❌ incrementQuantityWithAPI error:", error);
      return rejectWithValue(error.message || "Failed to update quantity");
    }
  }
);

export const decrementQuantityWithAPI = createAsyncThunk(
  'cart/decrementQuantityWithAPI',
  async ({ productId, variationId, variantSizeId, platingId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const products = getState().cart.products;

      let product = products.find(
        p =>
          p.product_id === productId &&
          (p.product_variation_id ?? null) === (variationId ?? null) &&
          (p.variant_size_id ?? null) === (variantSizeId ?? null) &&
          (p.plating_id ?? null) === (platingId ?? null)
      );

      if (!product && variantSizeId) {
        product = products.find(
          p =>
            p.product_id === productId &&
            (p.product_variation_id ?? null) === (variationId ?? null) &&
            p.wholesaleSelections?.some(
              s => s.variantSizeId === variantSizeId && 
                   (s.plating_id ?? null) === (platingId ?? null)
            )
        );
      }

      if (!product) {
        console.error("Product not found in Redux store:", { productId, variationId, variantSizeId, platingId });
        throw new Error("Product not found");
      }

      let currentQty = 1;
      if (product.wholesaleSelections && variantSizeId) {
        const sizeSelection = product.wholesaleSelections.find(
          s => s.variantSizeId === variantSizeId && 
               (s.plating_id ?? null) === (platingId ?? null)
        );
        currentQty = sizeSelection?.qty || 1;
      } else {
        currentQty = product.quantity || 1;
      }

      // ✅ FIX: when qty === 1, dispatch removeItemFromCartWithAPI (which uses
      // removeItemFromCart WITH the user_id filter via the thunk) instead of
      // calling the raw helper directly (which was missing user_id and silently
      // returning without firing any mutation).
      if (currentQty === 1) {
        console.log("🗑️ Quantity is 1, removing item from cart");

        const result = await dispatch(removeItemFromCartWithAPI({
          product_id: productId,
          product_variation_id: variationId,
          variant_size_id: variantSizeId,
          plating_id: platingId,
          metal: product.metal || "",
        }));

        if (result.error) {
          throw new Error(result.payload || result.error.message || "Failed to remove item");
        }

        return { ...product, quantity: 0, removed: true };
      }

      // qty > 1 — reduce by 1 via the helper (this one does have user_id filter)
      console.log("🔻 Reducing quantity by 1");

      await reduceCartItemQuantity({
        product_id: productId,
        product_variation_id: variationId,
        variant_size_id: variantSizeId,
        plating_id: platingId,
        metal: product.metal || "",
      }, 1);

      dispatch(decrementQuantity({ productId, variationId, variantSizeId, platingId }));
      return { ...product, quantity: currentQty - 1 };

    } catch (error) {
      console.error("❌ decrementQuantityWithAPI error:", error);
      return rejectWithValue(error.message || "Failed to update quantity");
    }
  }
);

export const clearCartWithAPI = createAsyncThunk(
  'cart/clearCartWithAPI',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      console.log("🗑️ clearCartWithAPI called");
      await clearCartAPI();
      dispatch(clearCart());
      console.log("✅ Cart cleared successfully");
      return { success: true };
    } catch (error) {
      console.error("❌ clearCartWithAPI error:", error);
      return rejectWithValue(error.message || "Failed to clear cart");
    }
  }
);

const initialState = {
  products: [],
  cart_id: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const product = action.payload;
      
      if (product.wholesaleSelections && product.wholesaleSelections.length > 0) {
        const existing = state.products.find(p =>
          p.product_id === product.product_id &&
          (p.product_variation_id ?? null) === (product.product_variation_id ?? null)
        );

        if (existing) {
          if (!existing.wholesaleSelections && existing.variant_size_id) {
            console.log("🔄 Converting old cart format to wholesaleSelections");
            existing.wholesaleSelections = [{
              variantSizeId: existing.variant_size_id,
              ring_size: existing.ring_size,
              ring_inr_size: existing.ring_inr_size,
              plating_id: existing.plating_id ?? null,
              plating_name: existing.plating_name || "",
              plating_price_per_gram: existing.plating_price_per_gram || 0,
              qty: existing.quantity,
              metal: existing.metal || "",
              weight: existing.weight || 0,
              size_value: existing.size_value || null,
              display_size: existing.display_size || existing.ring_inr_size || existing.ring_size,
              original_stock: existing.original_stock || existing.maxQty || 0,
            }];
            existing.variant_size_id = null;
            existing.size = "Multiple";
          }

          if (existing.wholesaleSelections) {
            product.wholesaleSelections.forEach(newSelection => {
              const existingSelection = existing.wholesaleSelections.find(
                s => s.variantSizeId === newSelection.variantSizeId &&
                     (s.plating_id ?? null) === (newSelection.plating_id ?? null)
              );
              if (existingSelection) {
                existingSelection.qty += newSelection.qty;
              } else {
                existing.wholesaleSelections.push(newSelection);
              }
            });
            existing.quantity = existing.wholesaleSelections.reduce((sum, s) => sum + s.qty, 0);
          } else {
            state.products.push({ ...product });
          }
        } else {
          state.products.push({ ...product });
        }
      } else {
        const existing = state.products.find(p =>
          p.product_id === product.product_id &&
          (p.product_variation_id ?? null) === (product.product_variation_id ?? null) &&
          (p.variant_size_id ?? null) === (product.variant_size_id ?? null) &&
          (p.plating_id ?? null) === (product.plating_id ?? null)
        );

        if (existing) {
          existing.quantity += product.quantity;
          existing.price = (parseFloat(existing.unitPrice) * existing.quantity).toFixed(2);
        } else {
          state.products.push({ ...product });
        }
      }
    },

    removeFromCart: (state, action) => {
      const { product_id, product_variation_id, variant_size_id, plating_id } = action.payload;
      
      if (variant_size_id) {
        const product = state.products.find(
          p => p.product_id === product_id &&
               (p.product_variation_id ?? null) === (product_variation_id ?? null)
        );

        if (product?.wholesaleSelections) {
          product.wholesaleSelections = product.wholesaleSelections.filter(
            s => !(s.variantSizeId === variant_size_id && 
                   (s.plating_id ?? null) === (plating_id ?? null))
          );

          if (product.wholesaleSelections.length === 0) {
            state.products = state.products.filter(p => p !== product);
          } else {
            product.quantity = product.wholesaleSelections.reduce((sum, s) => sum + s.qty, 0);
          }
          return;
        }
      }

      state.products = state.products.filter(
        p =>
          !(p.product_id === product_id &&
            (p.product_variation_id ?? null) === (product_variation_id ?? null) &&
            (p.variant_size_id ?? null) === (variant_size_id ?? null) &&
            (p.plating_id ?? null) === (plating_id ?? null))
      );
    },

    clearCart: (state) => {
      state.products = [];
    },

    incrementQuantity: (state, action) => {
      const { productId, variationId, variantSizeId, platingId } = action.payload;
      
      const product = state.products.find(
        p => p.product_id === productId &&
             (p.product_variation_id ?? null) === (variationId ?? null)
      );

      if (!product) return;

      if (product.wholesaleSelections && variantSizeId) {
        const selection = product.wholesaleSelections.find(
          s => s.variantSizeId === variantSizeId && 
               (s.plating_id ?? null) === (platingId ?? null)
        );
        if (selection) {
          selection.qty += 1;
          product.quantity = product.wholesaleSelections.reduce((sum, s) => sum + s.qty, 0);
        }
      } else {
        product.quantity += 1;
        if (product.unitPrice) {
          product.price = (parseFloat(product.unitPrice) * product.quantity).toFixed(2);
        }
      }
    },

    decrementQuantity: (state, action) => {
      const { productId, variationId, variantSizeId, platingId } = action.payload;
      
      const product = state.products.find(
        p => p.product_id === productId &&
             (p.product_variation_id ?? null) === (variationId ?? null)
      );

      if (!product) return;

      if (product.wholesaleSelections && variantSizeId) {
        const selection = product.wholesaleSelections.find(
          s => s.variantSizeId === variantSizeId && 
               (s.plating_id ?? null) === (platingId ?? null)
        );
        if (selection && selection.qty > 1) {
          selection.qty -= 1;
          product.quantity = product.wholesaleSelections.reduce((sum, s) => sum + s.qty, 0);
        }
      } else {
        if (product.quantity > 1) {
          product.quantity -= 1;
          if (product.unitPrice) {
            product.price = (parseFloat(product.unitPrice) * product.quantity).toFixed(2);
          }
        }
      }
    },

    setCartId: (state, action) => {
      state.cart_id = action.payload;
    },

    setCartProducts: (state, action) => {
      state.products = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToCartWithAPI.fulfilled, (state, action) => {
        console.log("✅ Cart item added successfully");
      })
      .addCase(addToCartWithAPI.rejected, (state, action) => {
        console.error("❌ Cart item add failed:", action.payload);
      })
      .addCase(incrementQuantityWithAPI.fulfilled, (state, action) => {
        console.log("✅ Quantity increased successfully");
      })
      .addCase(incrementQuantityWithAPI.rejected, (state, action) => {
        console.error("❌ Increment failed:", action.payload);
      })
      .addCase(decrementQuantityWithAPI.fulfilled, (state, action) => {
        console.log("✅ Quantity decreased successfully");
      })
      .addCase(decrementQuantityWithAPI.rejected, (state, action) => {
        console.error("❌ Decrement failed:", action.payload);
      })
      .addCase(removeItemFromCartWithAPI.fulfilled, (state, action) => {
        console.log("✅ Item removed successfully");
      })
      .addCase(removeItemFromCartWithAPI.rejected, (state, action) => {
        console.error("❌ Remove item failed:", action.payload);
      })
      .addCase(clearCartWithAPI.pending, (state, action) => {
        console.log("⏳ Clearing cart...");
      })
      .addCase(clearCartWithAPI.fulfilled, (state, action) => {
        console.log("✅ Cart cleared successfully");
      })
      .addCase(clearCartWithAPI.rejected, (state, action) => {
        console.error("❌ Clear cart failed:", action.payload);
      });
  },
});

export const {
  addToCart,
  removeFromCart,
  clearCart,
  incrementQuantity,
  decrementQuantity,
  setCartId,
  setCartProducts,
} = cartSlice.actions;

export default cartSlice.reducer;